import { gravityInPlaneDegrees, gravityTiltDegrees } from './gravity-readings.js';
import { clamp, cross, DEG, dot, unit } from './vec3.js';
/**
 * How far the phone must move from its reference before the pivot is derived. Below a couple of
 * degrees, accelerometer noise dominates the cross product and the axis is meaningless (measured:
 * at 0.5 degrees of separation with 0.02g of noise the axis can be off by a full 90 degrees; at 5
 * degrees it is within 7, which costs 0.2 degrees of angle; at 10 degrees, within 3.5). Six is the
 * compromise — past the noise floor, but small enough that any real measurement gesture clears it
 * long before reaching an angle where the fold could matter.
 */
export const LATCH_SEPARATION_DEG = 6;
/**
 * The pivot's angular distance from the screen normal decides the gesture. A pivot lying in the
 * screen plane (distance ~90) is a TILT; a pivot along the screen normal (distance ~0) is a
 * SWEEP. The split is at 45 because the two ideals are exactly 90 apart, so nearest-wins needs no
 * tuning — `gestureMarginDegrees` reports how clean the match actually was.
 */
const GESTURE_SPLIT_DEG = 45;
/**
 * Calibrating with the screen tipped past this is refused. Beyond it the anchor's own `acos` sits
 * on the far side of the fold, so it would read (360 - true) and offset every later reading with
 * no way for the sensor to notice. 135 leaves flat, tilted and on-edge starts all comfortably
 * legal, and rejects only genuinely face-down attitudes.
 */
export const CALIBRATION_FACE_DOWN_LIMIT_DEG = 135;
/**
 * How near to horizontal the screen normal must be at CALIBRATION for the reading to start out as a
 * sweep rather than a tilt.
 *
 * The pivot cannot be derived until the phone has moved LATCH_SEPARATION_DEG, and until then the
 * only quantity available was the clinometer reading — which for an upright phone is 90 and stays
 * 90 for the whole rotation. So a student who calibrated a phone on a rod and turned it three
 * degrees was shown 90, and could capture it.
 *
 * The posture at calibration settles that window. It is a GUESS, not a derivation: a phone held
 * flat against a near-vertical rock face looks identical to one clamped to a level rod. The trade
 * is deliberate, and it is not the one it first appears to be — that in-place flow is already
 * destroyed today by any in-plane hand wobble, which latches as a sweep and reports the wobble
 * (measured: an 88 degree face reads 8 after an 8 degree tremor). So this does not sacrifice a
 * working case for a broken one; it fixes the main sweep flow at the cost of a flow that is
 * off-procedure and already fragile.
 *
 * Narrow at 10 degrees because a rod apparatus is built in a vertical plane and a real sweep sits
 * at 90 exactly, while every surface shallower than 80 degrees stays a tilt. And it only governs
 * the pre-latch window: six degrees of any real motion derives the pivot, which overrides it.
 */
export const SWEEP_POSTURE_BAND_DEG = 10;
/** Wraps into [0, 360). Shared shape with rotation.ts's own domain handling. */
const norm360 = (deg) => ((deg % 360) + 360) % 360;
/**
 * Creates a tracker. Stateful by design — unwrapping past +/-180 is inherently sequential — but
 * the state is only ever a reference sample, a latch, and an integer turn count. Nothing is
 * integrated, so nothing drifts: every reading is computed fresh from two absolute samples.
 */
export function createRotationTracker() {
    let reference = null;
    /** The reference's own absolute tilt, which the tilt gesture's reading is measured from. */
    let anchorTilt = 0;
    /**
     * The reference's own in-plane direction, which the SWEEP gesture's reading is measured from.
     *
     * This is the one place Calibrate sets a zero, and the asymmetry with tilt is the whole point.
     * The tilt reading is absolute about the WORLD — the screen normal against vertical — so zeroing
     * it would throw away a real measurement and silently offset every answer, which is the failure
     * this tracker has always refused. The sweep reading is absolute about the PHONE: it is the long
     * axis against horizontal, which equals the rod's angle only if the phone happens to be clamped
     * with its long edge along the rod. There is no world reference in it to protect.
     *
     * So the same phone on the same rod read 0 clamped one way and 90 clamped the other, and nothing
     * in the gravity vector could tell which — the block simply reported the student's answer a
     * quarter turn out. Measuring from the reference removes the assumption instead of guessing it:
     * whatever the clamp, the attitude at Calibrate is zero.
     *
     * Null when the phone is flat at calibration, where the in-plane direction is meaningless. That
     * cannot reach the sweep branch anyway: a sweep from flat never moves gravity at all, so no pivot
     * is ever derived.
     */
    let anchorInPlane = null;
    /** Whether the calibration posture says to read a sweep until the pivot can say otherwise. */
    let anchorSweep = false;
    let latch = null;
    let lastRawTheta = null;
    let turnOffset = 0;
    let latest = null;
    let streamBroken = false;
    function idle() {
        return {
            state: 'uncalibrated',
            degrees: null,
            gesture: null,
            gestureMarginDegrees: 0,
            offAxisDegrees: 0,
            turns: 0,
            streamBroken,
        };
    }
    function tryLatch(g) {
        // Never re-latch after a break. The pivot would come back correct but the turn count would
        // not, and the pair would read as a trustworthy 160 where the truth is 200.
        if (!reference || latch || streamBroken)
            return;
        const separation = Math.acos(clamp(dot(reference, g), -1, 1)) * DEG;
        if (separation < LATCH_SEPARATION_DEG)
            return;
        const pivot = unit(cross(reference, g));
        if (!pivot)
            return;
        // Distance from the screen normal decides the gesture. abs() because the pivot's sign is a
        // consequence of which way the student happened to rotate, not of the configuration.
        const fromNormal = Math.acos(clamp(Math.abs(pivot.z), -1, 1)) * DEG;
        const gesture = fromNormal > GESTURE_SPLIT_DEG ? 'tilt' : 'sweep';
        const ideal = gesture === 'tilt' ? 90 : 0;
        // Which way does a positive theta run? Compare the tilt at latch against the anchor: if the
        // phone tilted further from vertical during the latch motion, positive theta means more tilt.
        // In a sweep the tilt does not change at all, so this tie is arbitrary and unused — the sweep
        // reading never consults `sign`.
        const tiltNow = gravityTiltDegrees(g) ?? anchorTilt;
        const rawTheta = Math.atan2(dot(cross(reference, g), pivot), dot(reference, g)) * DEG;
        const tiltIncreased = tiltNow >= anchorTilt;
        const sign = rawTheta >= 0 === tiltIncreased ? 1 : -1;
        latch = {
            pivot,
            gesture,
            gestureMarginDegrees: Math.abs(fromNormal - ideal),
            invariant: dot(reference, pivot),
            sign,
        };
        lastRawTheta = rawTheta;
        turnOffset = 0;
    }
    function update(raw) {
        const g = unit(raw);
        if (!g)
            return snapshot();
        latest = g;
        if (!reference)
            return idle();
        tryLatch(g);
        if (!latch) {
            /**
             * Pre-latch. WHICH quantity to report is settled by the posture at calibration — see
             * `SWEEP_POSTURE_BAND_DEG` — because the pivot that would settle it properly does not exist
             * yet and the wrong choice is not a rounding error but a different number entirely.
             *
             * Calibrated upright: report the sweep, measured from the calibration attitude, which is
             * correct from the first degree and for the full circle.
             *
             * Calibrated anywhere else: report the clinometer reading. Exact below 180, and unreachable
             * above it — a phone that has not moved LATCH_SEPARATION_DEG from its reference has not
             * crossed the fold.
             */
            const inPlane = anchorSweep && anchorInPlane != null ? gravityInPlaneDegrees(g) : null;
            return {
                state: streamBroken ? 'stream-broken' : 'awaiting-motion',
                degrees: inPlane != null ? norm360(inPlane - anchorInPlane) : (gravityTiltDegrees(g) ?? null),
                // Reported so a caller is not told "no gesture" while being handed a sweep reading. It is
                // posture-inferred rather than derived, which is why the margin below stays zero.
                gesture: inPlane != null ? 'sweep' : null,
                gestureMarginDegrees: 0,
                offAxisDegrees: 0,
                turns: 0,
                streamBroken,
            };
        }
        // Signed rotation from the reference about the derived pivot, in (-180, 180].
        const rawTheta = Math.atan2(dot(cross(reference, g), latch.pivot), dot(reference, g)) * DEG;
        // Unwrap. The jump can only be a wrap if it is larger than half a turn between samples, which
        // needs the phone to be spinning implausibly fast at any realistic sample rate. The count is
        // an integer, so it cannot drift — it can only be missed, and only by aliasing.
        if (lastRawTheta != null) {
            const delta = rawTheta - lastRawTheta;
            if (delta > 180)
                turnOffset -= 360;
            else if (delta < -180)
                turnOffset += 360;
        }
        lastRawTheta = rawTheta;
        const theta = rawTheta + turnOffset;
        let degrees;
        if (latch.gesture === 'sweep') {
            // Complete over a full circle from one sample; the pivot is only used here for the off-axis
            // check and the turn count. Measured FROM the reference — see `anchorInPlane`.
            const inPlane = gravityInPlaneDegrees(g);
            degrees = inPlane != null
                ? norm360(inPlane - (anchorInPlane ?? 0))
                : norm360(anchorTilt + latch.sign * theta);
        }
        else {
            // A signed rotation coordinate: 0 is flat, 180 is face-down, negative means tilted the
            // opposite way from the initial motion. The reported angle is its magnitude, because tilt
            // away from horizontal is direction-agnostic — rotating 10 degrees "the other way" is still
            // a 10 degree tilt, not 350. Overturn past 180 survives, since |190| is 190.
            degrees = norm360(Math.abs(anchorTilt + latch.sign * theta));
        }
        const spanned = Math.abs(anchorTilt + latch.sign * theta);
        return {
            state: 'tracking',
            degrees,
            gesture: latch.gesture,
            gestureMarginDegrees: latch.gestureMarginDegrees,
            // Gravity's component along the pivot is invariant under rotation about it. asin turns the
            // dot-product drift back into an indicative angle; it is a magnitude, not an exact attitude.
            offAxisDegrees: Math.asin(clamp(Math.abs(dot(g, latch.pivot) - latch.invariant), 0, 1)) * DEG,
            turns: Math.floor(spanned / 360),
            streamBroken,
        };
    }
    function snapshot() {
        if (!reference || !latest)
            return idle();
        return update(latest);
    }
    return {
        calibrate(raw) {
            const g = unit(raw);
            if (!g)
                return { ok: false, reason: 'no-signal' };
            const tilt = gravityTiltDegrees(g);
            if (tilt == null)
                return { ok: false, reason: 'no-signal' };
            if (tilt > CALIBRATION_FACE_DOWN_LIMIT_DEG)
                return { ok: false, reason: 'face-down' };
            reference = g;
            anchorTilt = tilt;
            anchorInPlane = gravityInPlaneDegrees(g);
            anchorSweep = Math.abs(tilt - 90) <= SWEEP_POSTURE_BAND_DEG;
            latch = null;
            lastRawTheta = null;
            turnOffset = 0;
            latest = g;
            streamBroken = false;
            return { ok: true };
        },
        update,
        reportStreamBreak() {
            streamBroken = true;
            latch = null;
            lastRawTheta = null;
            turnOffset = 0;
        },
        snapshot,
        reset() {
            reference = null;
            anchorTilt = 0;
            anchorInPlane = null;
            anchorSweep = false;
            latch = null;
            lastRawTheta = null;
            turnOffset = 0;
            latest = null;
            streamBroken = false;
        },
    };
}
//# sourceMappingURL=rotation-tracking.js.map